import { useState, useEffect, useRef } from "react";
import { base44 } from "@/api/base44Client";

// Cache offers & settings for the session (they rarely change)
let _offersCache = null;
let _settingsCache = null;
import SpinWheel from "@/components/wheel/SpinWheel";
import PhoneInput from "@/components/wheel/PhoneInput";
import OtpVerify from "@/components/wheel/OtpVerify";
import EmailOtpVerify from "@/components/wheel/EmailOtpVerify";
import CouponCard from "@/components/wheel/CouponCard";
import DisabledMessage from "@/components/wheel/DisabledMessage";
import WelcomeToast from "@/components/wheel/WelcomeToast";
import { useApp } from "@/lib/AppContext";
import SharedHeader from "@/components/SharedHeader";
import { t } from "@/lib/translations";

export default function CustomerPage() {
  const { lang } = useApp();
  const tr = t[lang];
  const [step, setStep] = useState("phone"); // phone | email_otp | welcome | wheel | result
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [phone, setPhone] = useState("");
  const phoneRef = useRef("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [spinResult, setSpinResult] = useState(null);
  const [offers, setOffers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [checkMsg, setCheckMsg] = useState("");

  const generateOtp = () => Math.floor(100000 + Math.random() * 900000).toString();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    // Use cached data if available to avoid redundant API calls
    if (_offersCache && _settingsCache) {
      setOffers(_offersCache);
      setSettings(_settingsCache);
      return;
    }
    const [offersList, settingsList] = await Promise.all([
      base44.entities.Offer.filter({ is_active: true }),
      base44.entities.Settings.list()
    ]);
    const activeOffers = offersList.filter(o => o.is_active);
    const settingsData = settingsList[0] || {
      spin_enabled: true,
      spins_per_phone: 1,
      days_between_spins: 30,
      campaign_name: tr.appName
    };
    _offersCache = activeOffers;
    _settingsCache = settingsData;
    setOffers(activeOffers);
    setSettings(settingsData);
  };

  const validatePhone = (p) => /^05\d{8}$/.test(p);

  const checkEligibility = (customer) => {
    if (customer && customer.last_spin_at) {
      const daysBetween = settings?.days_between_spins || 30;
      const lastSpin = new Date(customer.last_spin_at);
      const now = new Date();
      const diffDays = (now - lastSpin) / (1000 * 60 * 60 * 24);
      if (diffDays < daysBetween) {
        const nextDate = new Date(lastSpin.getTime() + daysBetween * 24 * 60 * 60 * 1000);
        return `${tr.alreadyParticipated} ${nextDate.toLocaleDateString(lang === "ar" ? 'ar-SA' : 'en-US')}`;
      }
    }
    return null;
  };

  // تحويل الرقم المحلي (05XXXXXXXX) إلى الصيغة الدولية (966XXXXXXXXX)
  const normalizePhone = (p) => p.startsWith("966") ? p : "966" + p.slice(1);

  const handlePhoneSubmit = async (phoneNum) => {
    setError("");
    if (!validatePhone(phoneNum)) {
      setError(tr.phoneError);
      return;
    }
    setLoading(true);
    try {
      const normalizedPhone = normalizePhone(phoneNum);

      // البحث بصيغتي الرقم (الدولية والمحلية) لمطابقة العملاء المستوردين
      let customers = await base44.entities.Customer.filter({ phone: normalizedPhone });
      if (customers.length === 0) {
        customers = await base44.entities.Customer.filter({ phone: phoneNum });
      }
      const customer = customers[0];
      // استخدام صيغة الرقم المخزنة في قاعدة البيانات، أو الصيغة الموحدة للعملاء الجدد
      const matchedPhone = customer ? customer.phone : normalizedPhone;

      // Check spin eligibility
      const eligibilityError = checkEligibility(customer);
      if (eligibilityError) {
        setCheckMsg(eligibilityError);
        return;
      }

      // Check active coupons (البحث بصيغتي الرقم)
      if (settings?.prevent_multiple_active_coupons) {
        const [c1, c2] = await Promise.all([
          base44.entities.Coupon.filter({ customer_phone: matchedPhone, status: "unused" }),
          base44.entities.Coupon.filter({ customer_phone: phoneNum, status: "unused" }),
        ]);
        if (c1.length > 0 || c2.length > 0) {
          setCheckMsg(tr.activeCoupon);
          return;
        }
      }

      // تعيين الرقم: العادي للعرض، الموحد لجميع العمليات البرمجية
      setPhone(phoneNum);
      phoneRef.current = matchedPhone;

      if (customer) {
        // عميل موجود → ترحيب ثم العجلة مباشرةً
        setIsNewCustomer(false);
        setStep("welcome");
      } else {
        // عميل جديد → توثيق البريد أولاً
        setStep("email_otp");
      }
    } finally {
      setLoading(false);
    }
  };

  const offersRef = useRef(offers);
  useEffect(() => { offersRef.current = offers; }, [offers]);

  const handleSpin = async (randomIdx) => {
    const currentPhone = phoneRef.current;
    let currentOffers = offersRef.current;

    if (!currentPhone) return null;

    try {
      // ── حماية من التكرار: تأكد أن العميل مسجل وأنه لم يلف مسبقاً ──
      const [customerRows, existingSpins] = await Promise.all([
        base44.entities.Customer.filter({ phone: currentPhone }),
        base44.entities.Spin.filter({ customer_phone: currentPhone })
      ]);

      // إذا لم يوجد العميل (نادر جداً) → اخرج بأمان
      if (customerRows.length === 0) return null;

      const customer = customerRows[0];
      const daysBetween = settings?.days_between_spins || 30;

      // فحص إذا لف مسبقاً ضمن الفترة المسموحة
      if (existingSpins.length > 0) {
        const lastSpinDate = new Date(Math.max(...existingSpins.map(s => new Date(s.created_date))));
        const diffDays = (new Date() - lastSpinDate) / (1000 * 60 * 60 * 24);
        if (diffDays < daysBetween) return null;
      }
      // ── نهاية الحماية ──

      let activeOffers = currentOffers.filter(o => {
        if (!o.is_active) return false;
        const now = new Date();
        if (o.start_date && new Date(o.start_date) > now) return false;
        if (o.end_date && new Date(o.end_date) < now) return false;
        return true;
      });

      if (activeOffers.length === 0) {
        const fresh = await base44.entities.Offer.filter({ is_active: true });
        activeOffers = fresh.filter(o => o.is_active);
        if (activeOffers.length === 0) return null;
        setOffers(activeOffers);
        offersRef.current = activeOffers;
      }

      // استخدم الـ index الذي اختارته العجلة (مُمرَّر من SpinWheel) لأخذ العرض
      const selectedOffer = activeOffers[randomIdx % activeOffers.length];

      // Generate coupon code
      const prefix = selectedOffer.offer_type === 'discount_percent' ? 'DSC' :
                     selectedOffer.offer_type === 'free_delivery' ? 'DEL' :
                     selectedOffer.offer_type === 'free_service' ? 'FREE' :
                     selectedOffer.offer_type === 'no_luck' ? 'LUCK' : 'OFF';
      const random4 = Math.random().toString(36).substring(2, 6).toUpperCase();
      const code = `${prefix}-${random4}`;

      const validityDays = selectedOffer.validity_days || settings?.default_coupon_validity_days || 30;
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + validityDays);

      let couponId = null;
      if (selectedOffer.offer_type !== 'no_luck') {
        const coupon = await base44.entities.Coupon.create({
          code,
          offer_id: selectedOffer.id,
          offer_title: selectedOffer.title,
          customer_phone: currentPhone,
          status: "unused",
          issued_at: new Date().toISOString(),
          expires_at: expiresAt.toISOString()
        });
        couponId = coupon.id;
      }

      // تحديث بيانات العميل (customer مُجلَب بالفعل في بداية الدالة)
      await base44.entities.Customer.update(customer.id, {
        total_spins: (customer.total_spins || 0) + 1,
        last_spin_at: new Date().toISOString(),
        last_coupon_code: selectedOffer.offer_type !== 'no_luck' ? code : customer.last_coupon_code,
        total_coupons_unused: (customer.total_coupons_unused || 0) + (selectedOffer.offer_type !== 'no_luck' ? 1 : 0),
      });

      await base44.entities.Spin.create({
        customer_phone: currentPhone,
        offer_id: selectedOffer.id,
        offer_title: selectedOffer.title,
        coupon_id: couponId,
        coupon_code: code
      });

      const result = {
        offer: selectedOffer,
        code: selectedOffer.offer_type !== 'no_luck' ? code : null,
        expiresAt: expiresAt.toISOString(),
        offerIndex: offersRef.current.findIndex(o => o.id === selectedOffer.id)
      };
      
      return result;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const handleSpinComplete = (result) => {
    setSpinResult(result);
    setStep("result");
  };

  if (!settings) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900">
        <div className="w-full bg-white/80 backdrop-blur-sm shadow-sm py-4 px-6 flex items-center gap-3">
          <img src="https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png" alt="" className="h-14 w-14 object-contain" />
          <div className="space-y-1.5">
            <div className="h-4 w-32 bg-muted animate-pulse rounded" />
            <div className="h-3 w-24 bg-muted animate-pulse rounded" />
          </div>
        </div>
        <div className="container max-w-lg mx-auto px-4 py-16 flex flex-col items-center gap-6">
          <div className="w-64 h-64 rounded-full bg-muted animate-pulse" />
          <div className="h-12 w-48 bg-muted animate-pulse rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!settings.spin_enabled) {
    return <DisabledMessage message={settings.spin_disabled_message} campaignName={settings.campaign_name} />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col relative overflow-hidden" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Animated background */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        
        {/* Floating animated boxes */}
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-gradient-to-bl from-cyan-500/15 to-transparent rounded-full blur-3xl animate-pulse" 
             style={{ animationDuration: '4s' }} />
        <div className="absolute top-1/3 -left-16 w-56 h-56 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-3xl blur-2xl animate-pulse" 
             style={{ animationDuration: '5s', animationDelay: '1s' }} />
        <div className="absolute -bottom-16 right-1/4 w-64 h-64 bg-gradient-to-tl from-purple-500/10 to-transparent rounded-full blur-3xl animate-pulse" 
             style={{ animationDuration: '6s', animationDelay: '2s' }} />
        
        {/* Floating particles */}
        <div className="absolute top-20 left-1/4 w-20 h-20 bg-cyan-400/15 rounded-full blur-xl animate-bounce" 
             style={{ animationDuration: '3s' }} />
        <div className="absolute bottom-40 right-1/3 w-24 h-24 bg-blue-400/10 rounded-full blur-xl animate-bounce" 
             style={{ animationDuration: '4s', animationDelay: '0.5s' }} />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" 
             style={{ 
               backgroundImage: 'linear-gradient(rgba(30, 95, 168, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(30, 95, 168, 0.15) 1px, transparent 1px)',
               backgroundSize: '50px 50px' 
             }} />
      </div>

      <SharedHeader />
      <div className="flex-1 overflow-auto px-4 py-6 pb-8 pt-20 relative z-10" style={{ maxWidth: 480, margin: "0 auto", width: "100%" }}>
        {step === "phone" && (
          <PhoneInput
            onSubmit={handlePhoneSubmit}
            loading={loading}
            error={error}
            checkMsg={checkMsg}
            onClearMsg={() => setCheckMsg("")}
          />
        )}

        {step === "email_otp" && (
          <EmailOtpVerify
            phone={phone}
            onVerified={async (email) => {
              // التحقق من أن البريد الإلكتروني غير مستخدم مسبقًا
              const existingWithEmail = await base44.entities.Customer.filter({ email });
              if (existingWithEmail.length > 0) {
                return { error: true, message: "هذا البريد الإلكتروني مسجل مسبقًا برقم جوال آخر. يرجى استخدام بريد إلكتروني مختلف." };
              }
              // التأكد من أن رقم الجوال لم يُسجَّل بينما كان المستخدم في شاشة التحقق
              const doubleCheck = await base44.entities.Customer.filter({ phone: phoneRef.current });
              if (doubleCheck.length > 0) {
                // الرقم تسجل فجأة (race condition) → تعامل معه كعميل موجود
                setCustomerEmail(email);
                setIsNewCustomer(false);
                setStep("welcome");
                return { error: false };
              }
              // تسجيل العميل الجديد فور التحقق من البريد لضمان وجوده قبل اللف
              setCustomerEmail(email);
              await base44.entities.Customer.create({
                phone: phoneRef.current,
                email,
                total_spins: 0,
                total_coupons_used: 0,
                total_coupons_unused: 0
              });
              setIsNewCustomer(true);
              setStep("welcome");
              return { error: false };
            }}
            onBack={() => { setStep("phone"); setPhone(""); }}
          />
        )}

        {step === "welcome" && (
          <WelcomeToast
            isNewCustomer={isNewCustomer}
            onDone={() => setStep("wheel")}
          />
        )}

        {step === "wheel" && (
          <SpinWheel
            offers={offers}
            onSpin={handleSpin}
            onSpinComplete={handleSpinComplete}
            phone={phone}
          />
        )}

        {step === "result" && spinResult && (
          <CouponCard
            result={spinResult}
            phone={phone}
            onNewSpin={() => { setStep("phone"); setPhone(""); setOtpCode(""); setSpinResult(null); setCheckMsg(""); }}
          />
        )}
      </div>
    </div>
  );
}